import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import './LoanOffer.scss'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import getApplicationServices from 'services/getApplicationServices'
import documentServices from 'services/documentServices'
import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { FormFactory } from 'formBuilder/formFactory'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerForm } from 'constants/enums/formTypes'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationInformation } from 'interfaces/i-application-information'
import {
	FormBuilder,
	formKeySelector,
	validateFormDataAction,
} from 'formBuilder/src'
import updateApplicationServices from 'services/updateApplicationServices'

const LoanOfferInvolveSpouseLt = () => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()
	const [disabled, setDisabled] = useState<boolean>(true)
	const applicationId = loanApplication.applicationData?.loanNumber!
	const dispatch = useDispatch()

	const [movedToNextStep, setMovedToNextStep] = useState<boolean>(false)

	let involveSpouseFormSchema = FormFactory(
		CustomerForm.InvolveSpouseForm,
		{
			formName: 'involveSpouseForm',
			blockName: 'involveSpouseForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation
	)

	const involveSpouseFormStateValues = useSelector(
		formKeySelector(
			involveSpouseFormSchema.formMeta.formName,
			'controlsState'
		)
	)

	const getApplicationInformation = async (applicationId: number) => {
		setLoading(true)
		try {
			await getApplicationServices
				.GetApplicationInformation(applicationId, t)
				.then((response) => {
					loanApplication.setApplicationData(response)
				})
			await documentServices
				.getDownloadDocuments(applicationId.toString(), t)
				.then((response) => {
					loanApplication.setDocumentData(response)
				})
		} catch (error) {
			console.warn(error)
			setError(error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (
			!loanApplication.updateApplicationData?.loanData
				?.maxInstalmentWarning
		) {
			setDisabled(false)
		} else {
			setDisabled(true)
		}
	}, [loanApplication.updateApplicationData?.loanData])

	useEffect(() => {
		if (
			!movedToNextStep &&
			loanApplication.applicationData?.signingUrls?.spouseConsent?.url
		) {
			next()
		}
	}, [loanApplication])

	const next = () => {
		setMovedToNextStep(true)
		dispatchMsp({
			type: MSP_ACTIONS.SET_INDEX,
			payload: {
				index: msp.index + 1,
			},
		})
	}

	const updateSpouseInfo = (
		ssn: string | undefined,
		firstName: string | undefined,
		lastName: string | undefined,
		mobilePhoneNumber: string | undefined,
		email: string | undefined
	) => {
		dispatchLoanApplication({
			type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
			payload: {
				...loanApplication,
				updateApplicationData: {
					...loanApplication.updateApplicationData,
					spouse: {
						...loanApplication.updateApplicationData?.spouse,
						ssn: ssn,
						firstName: firstName,
						lastName: lastName,
						cellPhone: mobilePhoneNumber,
						email: email,
						monthlyIncome: 0,
						mortgageMonthlyCost: 0,
						missedObligations: 0,
						otherMonthlyCost: 0,
					},
				},
			},
		})
	}

	useEffect(
		() => {
			updateSpouseInfo(
				involveSpouseFormStateValues?.ssn.value,
				involveSpouseFormStateValues?.firstName.value,
				involveSpouseFormStateValues?.lastName.value,
				involveSpouseFormStateValues?.mobilePhoneNumber.value,
				involveSpouseFormStateValues?.email.value
			)
		},
		[involveSpouseFormStateValues] // eslint-disable-line react-hooks/exhaustive-deps
	)

	const registerSpouseData = async () => {
		dispatch(validateFormDataAction('involveSpouseForm'))
		if (
			!involveSpouseFormStateValues?.ssn.isValid ||
			!involveSpouseFormStateValues?.firstName.isValid ||
			!involveSpouseFormStateValues?.lastName.isValid ||
			!involveSpouseFormStateValues?.mobilePhoneNumber.isValid ||
			!involveSpouseFormStateValues?.email.isValid
		) {
			return
		}

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.registerSpouse(
					applicationId,
					loanApplication.updateApplicationData!,
					t
				)
				.then(async (response) => {
					await getApplicationInformation(applicationId).then(() => {
						dispatchMsp({
							type: MSP_ACTIONS.RESET_STEPS,
						})
					})
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	return (
		<div className="loan-offer">
			{loading ? (
				<div className="col-spinner">
					<CircleSpinner />
				</div>
			) : (
				<>
					{loanApplication.applicationData?.offer === undefined ? (
						<ErrorBox message={`${t('LoanInfoError')}`} />
					) : (
						<>
							{involveSpouseFormSchema ? (
								<FormBuilder
									formSchema={involveSpouseFormSchema}
									apiError={undefined}
									showErrorsInForm={true}
									showSubmitButton={false}
								/>
							) : (
								<CircleSpinner />
							)}
							{error ? (
								<ErrorBox message={t('ErrorMsg')} />
							) : null}
							<div className="loan-offer-next-button">
								<button
									className="btn btn-primary"
									disabled={disabled}
									onClick={() => {
										setTimeout(() => {
											registerSpouseData()
										}, 400)
									}}>
									{t('Next')}
								</button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	)
}

export default LoanOfferInvolveSpouseLt
